import React, { type ReactNode } from 'react';

import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	useObjectStoreState,
	useAttribute,
} from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';

export type Props = {
	isColumn?: boolean;
	label: string;
	icon?: ReactNode;
	children: ReactNode;
	isMultiSelect: boolean;
	attributeTypeId: string;
};

export const EditableAttribute = (props: Props) => {
	const [{ hideEmptyAttributes }] = useObjectStoreState();
	const [attribute] = useAttribute(props.attributeTypeId);

	// attribute will be undefined if the attribute is empty
	if (hideEmptyAttributes && attribute === undefined) {
		return null;
	}

	return (
		<Box
			xcss={[
				AttributeWrapperStyles,
				props.isColumn && AttributeWrapperColumnStyles,
				props.isMultiSelect && AttributeWrapperMultiSelectStyles,
			]}
			testId="servicedesk-insight-editable-attribute-list.ui.editable-attribute.attribute-wrapper"
		>
			<Inline
				xcss={[AttributeLabelStyles, props.isColumn && AttributeLabelColumnStyles]}
				space="space.025"
			>
				{props.icon}
				{props.label}
			</Inline>
			<Box
				aria-label={props.label}
				xcss={[AttributeContentStyles, props.isColumn && AttributeContentColumnStyles]}
			>
				{props.children}
			</Box>
		</Box>
	);
};

const AttributeWrapperStyles = xcss({
	display: 'flex',
	wordBreak: 'break-word',
	alignItems: 'center',
	flexWrap: 'nowrap',
	flexDirection: 'row',
});

const AttributeWrapperMultiSelectStyles = xcss({
	flexWrap: 'wrap',
});

const AttributeWrapperColumnStyles = xcss({
	flexDirection: 'column',
	alignItems: 'flex-start',
	flexWrap: 'wrap',
});

const AttributeContentStyles = xcss({
	flexGrow: 1,
	flexBasis: '60%',
	boxSizing: 'border-box',
	maxWidth: '100%',
	overflowY: 'visible',
	overflowX: 'clip',
});

const AttributeLabelStyles = xcss({
	font: token('font.heading.xsmall'),
	color: 'color.text.subtle',
	maxWidth: '170px',
	flexBasis: '40%',
	margin: 'space.0',
	paddingBlock: 'space.100',
	lineHeight: '24px',
	marginTop: 'space.100',
	marginRight: 'space.100',
});

const AttributeLabelColumnStyles = xcss({
	flexBasis: 'auto',
});

const AttributeContentColumnStyles = xcss({
	width: '100%',
});
